import React, { lazy, useMemo } from 'react';
import type { RouteObject } from 'react-router-dom';
import { pathToRegexp } from 'path-to-regexp';
import MainLayout from '../layouts/MainLayout';
import Home from '../pages/home';
import NoMatch from '../components/NoMatch';
import CriminalImg from '../assets/criminal.png';
import FamilyLawyerImg from '../assets/family_lawyer.png';
import CivilImg from '../assets/civil.png';
import RealestateImg from '../assets/realestate.png';
import Attorney130Img from '../assets/attorney130.png';

const BreadcrumbsLayout = lazy(() => import('../layouts/BreadcrumbsLayout'));
const About = lazy(() => import('../pages/about'));
const ServiceTemplate = lazy(() => import('../components/common/ServiceTemplate'));

export type Route = RouteObject & {
  name: string;
  imageUrl: string;
  children?: RouteObject['children'] & Route[];
};

const routes: Route[] = [
  {
    name: 'home',
    imageUrl: '',
    path: '/',
    element: <MainLayout />,
    children: [
      {
        name: 'home',
        imageUrl: '',
        index: true,
        element: <Home />,
      },
      {
        name: 'home',
        imageUrl: '',
        path: '/',
        element: <BreadcrumbsLayout />,
        children: [
          {
            name: 'lawyerTitle',
            imageUrl: '',
            path: '/about',
            element: <About />,
          },
          {
            name: 'services.criminalCases',
            imageUrl: CriminalImg,
            path: '/services/criminal-cases',
            element: (
              <ServiceTemplate
                textCount={9}
                title="criminalCases.title"
                subtitle="criminalCases.text"
                footerText="criminalCases.footerText"
              />
            ),
          },
          {
            name: 'services.familyCases',
            imageUrl: FamilyLawyerImg,
            path: '/services/family-cases',
            element: (
              <ServiceTemplate
                textCount={13}
                title="familyCases.title"
                subtitle="familyCases.text"
                footerText="familyCases.footerText"
              />
            ),
          },
          {
            name: 'services.realEstate',
            imageUrl: RealestateImg,
            path: '/services/real-estate',
            element: (
              <ServiceTemplate
                textCount={5}
                title="realEstate.title"
                subtitle="realEstate.text"
                footerText="realEstate.footerText"
              />
            ),
          },
          {
            name: 'services.administrativeCasesArticle130',
            imageUrl: Attorney130Img,
            path: '/services/administrative-cases-article-130',
            element: (
              <ServiceTemplate
                textCount={4}
                title="administrativeCasesArticle130.title"
                subtitle="administrativeCasesArticle130.text"
                footerText="administrativeCasesArticle130.footerText"
              />
            ),
          },
          {
            name: 'services.civilCases',
            imageUrl: CivilImg,
            path: '/services/civil-cases',
            element: (
              <ServiceTemplate
                textCount={7}
                // title="civilCases.title"
                subtitle="civilCases.text"
                footerText="civilCases.footerText"
              />
            ),
          },
          {
            name: 'services.europeanCourt',
            imageUrl: CivilImg,
            path: '/services/european-court',
            element: (
              <ServiceTemplate
                textCount={3}
                title="europeanCourt.title"
                subtitle="europeanCourt.text"
                footerText="europeanCourt.footerText"
              />
            ),
          },
        ],
      },
      { path: '*', name: '', imageUrl: '', element: <NoMatch /> },
    ],
  },
];

export default function getRoutes(): Route[] {
  return routes;
}

const routeItems = getRoutes()[0]?.children || [];

export function useLawyerServices(routeList: Route[] = routeItems): Route[] {
  function getLawyerServices(list: Route[] = routes): Route[] {
    return list.reduce((acc: Route[], curr: Route) => {
      if (curr.path === '*') {
        return acc;
      }
      if (curr?.path?.includes('/services')) {
        return [...acc, curr];
      }
      if (curr.children) {
        if (curr.path === '/') {
          return [...acc, ...getLawyerServices(curr.children)];
        }
        return [...acc, curr, ...getLawyerServices(curr.children)];
      }
      return acc;
    }, []);
  }
  // eslint-disable-next-line
  return useMemo(() => getLawyerServices(routeList), [routeList]);
}

export function useBreadcrumbs(
  pathname: string,
  routeList: Route[] = routeItems
): Route[] {
  const regexp = pathToRegexp(pathname);

  function getBreadcrumbs(list: Route[] = routes): Route[] {
    return list.reduce((acc: Route[], curr: Route) => {
      if (curr.path && regexp.test(curr.path)) {
        return [...acc, curr];
      }
      if (curr.children) {
        return [...acc, curr, ...getBreadcrumbs(curr.children)];
      }
      return acc;
    }, []);
  }
  // eslint-disable-next-line
  return useMemo(() => getBreadcrumbs(routeList), [pathname, routeList]);
}
